import { Clock, CheckCircle, FileText, Calendar, AlarmClock, BookOpen, ArrowRight, ArrowLeft } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { useNavigate, useParams } from 'react-router-dom';
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { BonusInstructions, InstructionsComponent } from './Instructions';
import { DarkModeToggle } from '../../Layout/DarkModeToggle';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from '../../components/ui/dialog';

export default function LockedTest() {
  const { packId, testId } = useParams();
  const [testData, setTestData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('syllabus');
  const navigate = useNavigate();
  useEffect(() => {
    const fetchTestData = async () => {
      if (!packId || !testId) {
        setLoading(false);
        setError('Pack ID or Test ID not found in URL.');
        return;
      }

      setLoading(true);
      try {
        const response = await axios.get(`/api/test/${packId}/${testId}`);
        setTestData(response.data);
        setError(null);
      } catch (err) {
        console.error('Error fetching test data:', err);
        setError('Failed to fetch test details. Please try again later.');
        setTestData(null);
      } finally {
        setLoading(false);
      }
    };

    fetchTestData();
  }, [packId, testId]);

  const getCardDetails = () => {
    if (!testData) return [];

    const startDate = testData.startDate
      ? new Date(testData.startDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })
      : 'N/A';
    const totalSubjects = testData.subjects ? testData.subjects.length : 0;

    return [
      {
        icon: AlarmClock,
        iconBg: 'var(--q3-blue-light)',
        iconColor: 'var(--q3-blue-normal)',
        value: `${testData.totalTime} min`,
        label: 'Total time for test',
      },
      {
        icon: CheckCircle,
        iconBg: 'var(--q3-green-light)',
        iconColor: 'var(--q3-green-normal)',
        value: `${testData.totalQuestions} Qs`,
        label: 'Total Qs for test',
      },
      {
        icon: FileText,
        iconBg: 'var(--q3-purple-light)',
        iconColor: 'var(--q3-purple-normal)',
        value: `${testData.totalMarks}`,
        label: 'Total score for test',
      },
      {
        icon: Calendar,
        iconBg: 'var(--q3-orange-light)',
        iconColor: 'var(--q3-orange-normal)',
        value: startDate,
        label: 'Date of test release',
      },
      {
        icon: BookOpen,
        iconBg: 'var(--q3-indigo-light)',
        iconColor: 'var(--q3-indigo-normal)',
        value: `${totalSubjects}`,
        label: 'Total Subject',
      },
    ];
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[var(--q3-surface-default)] p-8 flex justify-center items-center">Loading...</div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[var(--q3-surface-default)] p-8 flex justify-center items-center text-red-500">
        {error}
      </div>
    );
  }
  const actualTestData = testData;

  const getSubjectTitle = (subjectId) => {
    const subject = actualTestData.subjects?.find((s) => s.subjectId._id === subjectId);
    return subject ? subject.subjectId.title : 'N/A';
  };

  const structuredExamContent = {};
  actualTestData.sections?.forEach((section) => {
    const subjectTitle = getSubjectTitle(section.sectionId.subject);
    if (!structuredExamContent[subjectTitle]) {
      structuredExamContent[subjectTitle] = [];
    }

    const questionCounts = section.sectionId.questions.reduce((acc, q) => {
      acc[q.type] = (acc[q.type] || 0) + 1;
      return acc;
    }, {});

    structuredExamContent[subjectTitle].push({
      title: section.sectionId.title,
      questionTypes: questionCounts,
      markingScheme: {
        correct: section.sectionId.markingScheme?.correctAttempt || 0,
        incorrect: section.sectionId.markingScheme?.incorrectAttempt || 0,
      },
    });
  });

  return (
    <div className="min-h-screen relative bg-[var(--q3-surface-default)] p-8">
      <div>
        <DarkModeToggle />
      </div>

      <div className="max-w-5xl mx-auto">
        {/* Header Section */}
        <div
          onClick={() => navigate(`/v3/${packId}/tests`)}
          className="w-fit cursor-pointer p-2 mb-5 bg-[var(--q3-surface-dimmer)] rounded-full"
        >
          <ArrowLeft />
        </div>

        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-6 mb-6">
          <div className="flex-1">
            <h1 className="text-2xl lg:text-3xl font-bold text-[var(--q3-neutral-default)] mb-2">
              {actualTestData.title}
            </h1>
            <p className="text-[var(--q3-neutral-light)] text-sm">{actualTestData.description}</p>
          </div>
          <Dialog>
            <DialogTrigger asChild>
              <Button className="bg-[var(--q3-base-green)] hover:bg-[var(--q3-base-green)]/90">Attempt Test</Button>
            </DialogTrigger>
            <DialogContent className="w-[350px] md:w-[450px] flex flex-col items-center max-w-sm rounded-lg bg-[var(--q3-surface-default)] backdrop-blur-sm">
              <div className="rounded-lg  flex flex-col items-center justify-center text-center">
                <div className="mb-4">
                  <img src={testData?.exam?.[0]?.icon} alt="Quizrr Logo" className="h-12 w-12 object-contain" />
                </div>
                <DialogTitle className="text-md font-bold text-[var(--q3-accent-normal)]">
                  {actualTestData.packTitle}
                </DialogTitle>
                <DialogDescription className="text-3xl py-5 font-bold text-[var(--q3-neutral-default)]">
                  Unlock the test series to attempt the test
                </DialogDescription>
              </div>
              <Button
                onClick={() => window.open(actualTestData?.paymentLink, '_blank')}
                className="w-full py-8 flex items-center bg-[var(--q3-base-green)] hover:bg-[var(--q3-base-green)]/90 rounded-full"
              >
                <span className="text-lg">Purchase Tests Series</span> <ArrowRight />
              </Button>
              <p className="text-[var(--q3-neutral-light)] text-center text-base">
                You are about to unlock the
                <span className="font-bold text-[var(--q3-neutral-default)]"> MOST RELEVANT </span>
                test series for JEE Main
              </p>
            </DialogContent>
          </Dialog>
        </div>
        <div className="grid grid-cols-2  md:grid-cols-3 lg:grid-cols-5 gap-7 mb-8">
          {getCardDetails().map((card, index) => (
            <Card key={index} className="bg-[var(--q3-surface-default)] py-4 shadow-none">
              <CardContent className="py-0">
                <div className="flex items-center gap-2 mb-2">
                  <div
                    className="w-8 h-8 rounded-full flex items-center justify-center"
                    style={{ backgroundColor: card.iconBg }}
                  >
                    <card.icon className="w-4 h-auto" style={{ color: card.iconColor }} />
                  </div>
                </div>
                <div className="text-lg font-bold text-[var(--q3-neutral-default)] mb-1">{card.value}</div>
                <div className="text-sm text-[var(--q3-neutral-light)] leading-tight">{card.label}</div>
              </CardContent>
            </Card>
          ))}
        </div>

        <div className="mb-6">
          <div className="flex gap-8 border-b border-[var(--q3-stroke-normal)]">
            <button
              className={`pb-3 cursor-pointer px-1 text-sm font-medium ${activeTab === 'syllabus' ? 'text-[var(--q3-accent-normal)] border-b-2 border-[var(--q3-accent-normal)]' : 'text-[var(--q3-neutral-light)] hover:text-[var(--q3-neutral-default)]'}`}
              onClick={() => setActiveTab('syllabus')}
            >
              Syllabus
            </button>
            <button
              className={`pb-3 cursor-pointer px-1 text-sm font-medium ${activeTab === 'instructions' ? 'text-[var(--q3-accent-normal)] border-b-2 border-[var(--q3-accent-normal)]' : 'text-[var(--q3-neutral-light)] hover:text-[var(--q3-neutral-default)]'}`}
              onClick={() => setActiveTab('instructions')}
            >
              Instructions
            </button>
          </div>
        </div>

        <div className="space-y-4">
          {activeTab === 'syllabus' && (
            <>
              <Card className="bg-[var(--q3-surface-default)] shadow-none py-0">
                <CardContent className="p-4">
                  <div className="text-xl text-[var(--q3-neutral-default)] mb-1 font-bold">Syllabus</div>
                  {actualTestData.description && (
                    <p className="text-[var(--q3-neutral-light)] text-lg ">{actualTestData.description}</p>
                  )}
                  <div
                    dangerouslySetInnerHTML={{ __html: actualTestData.syllabus }}
                    className="text-[var(--q3-neutral-light)] text-lg mb-4"
                  />
                </CardContent>
              </Card>

              <Card className="bg-[var(--q3-surface-default)] shadow-none py-0">
                <CardContent className="p-4">
                  <div className="syllabus-content">
                    <div className="text-[var(--q3-neutral-default)] text-xl font-bold mb-1">
                      Exam Structure & Marking Scheme
                    </div>
                    <p className="text-[var(--q3-neutral-light)] text-lg mb-4">
                      The paper is divided into {actualTestData.sections?.length || 0} sections
                    </p>

                    {Object.entries(structuredExamContent).map(([subjectTitle, sectionsData]) => (
                      <div key={subjectTitle} className="mb-4">
                        <div className="flex items-center gap-2 mb-1">
                          {/* <div className="w-4 h-4 flex items-center justify-center">
                                                        {getSubjectIcon(subjectTitle)}
                                                    </div> */}
                          <div className="text-[var(--q3-neutral-default)] font-medium text-lg">{subjectTitle}</div>
                        </div>
                        <ul className="text-[var(--q3-neutral-light)] text-md space-y-1 ml-1">
                          {sectionsData.map((data, idx) => (
                            <React.Fragment key={idx}>
                              {data.questionTypes.numerical > 0 && (
                                <li>
                                  Numerical: {data.questionTypes.numerical} questions (
                                  {`+${data.markingScheme.correct} for correct, `}
                                  {`${data.markingScheme.incorrect} for incorrect, 0 for no response`})
                                </li>
                              )}
                              {data.questionTypes.singleCorrect > 0 && (
                                <li>
                                  Single Correct: {data.questionTypes.singleCorrect} questions (
                                  {`+${data.markingScheme.correct} for correct, `}
                                  {`${data.markingScheme.incorrect} for incorrect, 0 for no response`})
                                </li>
                              )}
                              {data.questionTypes.multipleCorrect > 0 && (
                                <li>
                                  Multiple Correct: {data.questionTypes.MultipleCorrect} questions (
                                  {`+${data.markingScheme.correct} for correct, `}
                                  {`${data.markingScheme.incorrect} for incorrect, 0 for no response`})
                                </li>
                              )}
                              {data.questionTypes.fillTheBlanks > 0 && (
                                <li>
                                  Fill in the Blanks Correct: {data.questionTypes.fillTheBlanks} questions (
                                  {`+${data.markingScheme.correct} for correct, `}
                                  {`${data.markingScheme.incorrect} for incorrect, 0 for no response`})
                                </li>
                              )}
                            </React.Fragment>
                          ))}
                        </ul>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </>
          )}

          {activeTab === 'instructions' && (
            <Card className="bg-[var(--q3-surface-default)] shadow-none py-2">
              <CardContent className="p-4">
                <InstructionsComponent
                  ctype={actualTestData.ctype}
                  chapterName={actualTestData.chapters}
                  description={actualTestData.description}
                  questionCount={actualTestData.totalQuestions}
                  totalTime={actualTestData.totalTime}
                  totalMarks={actualTestData.totalMarks}
                  sections={actualTestData.sections}
                  subjects={actualTestData.subjects}
                />

                {actualTestData.bonusSections &&
                  actualTestData.bonusSections.length > 0 &&
                  actualTestData.bonusQuestions > 0 && (
                    <div className="mt-6">
                      <h2 className="text-xl text-[var(--q3-neutral-default)] mb-1 font-bold">
                        Bonus Section Instructions
                      </h2>
                      <BonusInstructions
                        bonusSections={actualTestData.bonusSections}
                        questionsCount={actualTestData.bonusQuestions}
                      />
                    </div>
                  )}
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}
